"use client";

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';

import { getCurrentUserProfile, ApiError } from '../lib/api';
import type { UserProfile } from '../types/user';
import { useOnboardingStore } from '../lib/onboarding';

export const useCurrentUserProfile = () => {
  const { getToken, isSignedIn, signOut } = useAuth();
  const hydrateFromProfile = useOnboardingStore((state) => state.hydrateFromProfile);
  const hasHydratedRef = useRef(false);

  const queryResult = useQuery<UserProfile | null>({
    queryKey: ['current-user'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        return null;
      }
      return getCurrentUserProfile(token);
    },
    staleTime: 1000 * 60, // 1 minute
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (queryResult.error instanceof ApiError && (queryResult.error.status === 401 || queryResult.error.status === 403)) {
      console.log('Authentication error detected, signing out.');
      signOut();
    } else if (queryResult.isError && import.meta.env.DEV) {
      console.error('Failed to load current user profile', queryResult.error);
    }
  }, [queryResult.isError, queryResult.error, signOut]);

  useEffect(() => {
    if (!hasHydratedRef.current && queryResult.data) {
      hydrateFromProfile(queryResult.data as UserProfile);
      hasHydratedRef.current = true;
    }
  }, [queryResult.data, hydrateFromProfile]);

  return queryResult;
};
